/**
 * Episode attendee stacks.
 *
 * The faces shown against an episode — who has reserved a spot. Kept in a
 * shared duck rather than in either calendar page's own duck because both the
 * Creator and the Org calendar open the same episode pop-up, and the Live Room
 * surfaces will want the same data without a third copy of the fetch.
 *
 * Fetching is triggered from the event-click handler that opens the pop-up, so
 * a stack is only ever loaded for an episode somebody actually looked at.
 *
 * What comes back depends on who is asking: everyone entitled to see the
 * episode gets `total`, but `attendees` is populated only for a viewer who owns
 * that audience (see server/api-util/episodeRsvp.js). The client never decides
 * this — it just renders what the server was willing to send.
 */

import { storableError } from '../util/errors';
import { episodeRsvpAPI } from '../api/clients/episodeRsvpAPI';

// ================ Action types ================ //

export const FETCH_EPISODE_ATTENDEES_REQUEST =
  'app/episodeAttendees/FETCH_EPISODE_ATTENDEES_REQUEST';
export const FETCH_EPISODE_ATTENDEES_SUCCESS =
  'app/episodeAttendees/FETCH_EPISODE_ATTENDEES_SUCCESS';
export const FETCH_EPISODE_ATTENDEES_ERROR =
  'app/episodeAttendees/FETCH_EPISODE_ATTENDEES_ERROR';

// ================ Helpers ================ //

/** One episode is identified by its Live Room plus its own id. */
export const attendeeKey = (listingId, episodeId) => `${listingId}:${episodeId}`;

// ================ Reducer ================ //

const initialState = {
  // key -> { total, canSeeIdentities, avatarLimit, attendees }
  byEpisode: {},
  // key -> true while a fetch is in flight
  inProgress: {},
  // key -> storableError
  errors: {},
};

export default function episodeAttendeesReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_EPISODE_ATTENDEES_REQUEST:
      return {
        ...state,
        inProgress: { ...state.inProgress, [payload.key]: true },
        errors: { ...state.errors, [payload.key]: null },
      };
    case FETCH_EPISODE_ATTENDEES_SUCCESS:
      return {
        ...state,
        byEpisode: { ...state.byEpisode, [payload.key]: payload.data },
        inProgress: { ...state.inProgress, [payload.key]: false },
      };
    case FETCH_EPISODE_ATTENDEES_ERROR:
      return {
        ...state,
        inProgress: { ...state.inProgress, [payload.key]: false },
        errors: { ...state.errors, [payload.key]: payload.error },
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchEpisodeAttendeesRequest = key => ({
  type: FETCH_EPISODE_ATTENDEES_REQUEST,
  payload: { key },
});

export const fetchEpisodeAttendeesSuccess = (key, data) => ({
  type: FETCH_EPISODE_ATTENDEES_SUCCESS,
  payload: { key, data },
});

export const fetchEpisodeAttendeesError = (key, error) => ({
  type: FETCH_EPISODE_ATTENDEES_ERROR,
  payload: { key, error },
  error: true,
});

// ================ Thunks ================ //

/**
 * Load the avatar stack for one episode.
 *
 * A failure is recorded but never thrown onward: the stack is a secondary
 * detail in the pop-up, and losing it should not take the pop-up with it.
 */
export const fetchEpisodeAttendees = ({ listingId, episodeId }) => async dispatch => {
  if (!listingId || !episodeId) return null;
  const key = attendeeKey(listingId, episodeId);

  dispatch(fetchEpisodeAttendeesRequest(key));
  try {
    // `get` is axios, so the server's `{ data }` envelope sits inside the
    // response's own `data`. Reading only the outer one left `total` undefined,
    // and every pop-up said "No spots reserved yet".
    const response = await episodeRsvpAPI.attendees(listingId, episodeId);
    const body = response?.data ?? response;
    const data = body?.data ?? body;
    dispatch(fetchEpisodeAttendeesSuccess(key, data));
    return data;
  } catch (e) {
    dispatch(fetchEpisodeAttendeesError(key, storableError(e)));
    return null;
  }
};
